import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  Box,
  MenuItem,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";

export type ContentFieldType =
  | "string"
  | "integer"
  | "float"
  | "date"
  | "datetime"
  | "boolean"
  | "file"
  | "select";

export type SelectOption = {
  label: string;
  value: string | number | boolean;
};

export interface FieldConfig {
  label: string;

  // For "file", value is usually a URL/string path or null
  // For "select", value is the selected primitive
  value: string | number | boolean | File | null | undefined;

  field: string;
  type?: ContentFieldType;
  multiline?: boolean;
  rows?: number;

  // Only used for type="select"
  options?: SelectOption[];

  // Only used for type="file"
  accept?: string;
}

interface ContentDetailsProps {
  title?: string;
  fields: FieldConfig[];
  onChange?: (field: string, value: any) => void;
  onSave?: () => void;
  saving?: boolean;
  showSaveButton?: boolean;
  saveButtonColor?: string;
}

function toDateTimeLocal(value: any): string {
  if (!value) return "";

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return "";
  }

  const pad = (number: number) => number.toString().padStart(2, "0");

  return (
    `${date.getFullYear()}-` +
    `${pad(date.getMonth() + 1)}-` +
    `${pad(date.getDate())}T` +
    `${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}`
  );
}

function getFileDisplayValue(value: FieldConfig["value"]): string {
  if (value instanceof File) {
    return value.name;
  }

  if (typeof value === "string") {
    return value;
  }

  return "File uploaded";
}

function getFileName(value: FieldConfig["value"]): string {
  if (value instanceof File) {
    return value.name;
  }

  if (typeof value === "string") {
    const withoutQuery = value.split("?")[0];
    const segments = withoutQuery.split("/");
    return segments[segments.length - 1] || "View file";
  }

  return "File uploaded";
}

interface FileUploadFieldProps {
  field: FieldConfig;
  saving: boolean;
  onChange?: (field: string, value: any) => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  field,
  saving,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasFile = Boolean(field.value);

  const openFilePicker = () => {
    if (!saving) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Clear the input so selecting the same file again still triggers onChange.
    event.currentTarget.value = "";

    onChange?.(field.field, file);
  };

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!saving) {
      onChange?.(field.field, null);
    }
  };

  const fileDisplayValue = getFileDisplayValue(field.value);
  const fileName = getFileName(field.value);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(140px, 220px) minmax(0, 1fr)",
        },
        columnGap: {
          xs: 0,
          sm: 4,
        },
        rowGap: {
          xs: 1.5,
          sm: 0,
        },
        alignItems: "start",
        width: "100%",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          pt: {
            xs: 0,
            sm: 1.5,
          },
          fontWeight: 600,
          color: "text.primary",
        }}
      >
        {field.label}
      </Typography>

      <Box>
        <input
          ref={inputRef}
          type="file"
          accept={field.accept}
          hidden
          onChange={handleFileChange}
        />

        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            maxWidth: "100%",
          }}
        >
          <Tooltip
            title={hasFile ? "Upload a different file" : "Upload file"}
          >
            <Box
              component="button"
              type="button"
              disabled={saving}
              onClick={openFilePicker}
              aria-label={
                hasFile
                  ? `Replace ${field.label}`
                  : `Upload ${field.label}`
              }
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",

                width: {
                  xs: "100%",
                  sm: 210,
                },
                minWidth: {
                  xs: 0,
                  sm: 210,
                },
                minHeight: 145,

                px: 2.5,
                py: 2,

                color: hasFile ? "text.primary" : "text.secondary",
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",

                cursor: saving ? "default" : "pointer",
                opacity: saving ? 0.7 : 1,
                overflow: "hidden",

                transition: (theme) =>
                  theme.transitions.create(
                    [
                      "color",
                      "background-color",
                      "border-color",
                      "box-shadow",
                      "opacity",
                    ],
                    {
                      duration: theme.transitions.duration.short,
                    }
                  ),

                "&:hover": {
                  color: saving ? undefined : "primary.main",
                  borderColor: saving ? "divider" : "primary.main",
                  backgroundColor: saving
                    ? "background.paper"
                    : "action.hover",
                  boxShadow: saving ? 0 : 2,
                },

                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: "primary.light",
                  outlineOffset: 3,
                },

                "&:disabled": {
                  color: hasFile ? "text.primary" : "text.secondary",
                },
              }}
            >
              {hasFile ? (
                <>
                  <InsertDriveFileOutlinedIcon
                    sx={{
                      mb: 1,
                      fontSize: 52,
                    }}
                  />

                  <Typography
                    component="span"
                    variant="body2"
                    title={fileName}
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fileName}
                  </Typography>
                </>
              ) : (
                <>
                  <UploadFileIcon
                    sx={{
                      mb: 1,
                      fontSize: 48,
                    }}
                  />

                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Upload file
                  </Typography>
                </>
              )}

              {saving && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "inherit",
                    backgroundColor: "rgba(255, 255, 255, 0.72)",
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
            </Box>
          </Tooltip>

          {hasFile && (
            <Tooltip title="Remove file">
              <span>
                <IconButton
                  type="button"
                  size="small"
                  disabled={saving}
                  onClick={handleRemove}
                  aria-label={`Remove ${field.label}`}
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    zIndex: 2,
                    width: 30,
                    height: 30,
                    backgroundColor: "background.paper",
                    boxShadow: 2,

                    "&:hover": {
                      backgroundColor: "background.paper",

                      "& .MuiSvgIcon-root": {
                        color: "error.main",
                      },
                    },
                  }}
                >
                  <CloseIcon
                    sx={{
                      fontSize: 19,
                      color: "text.secondary",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ContentDetails: React.FC<ContentDetailsProps> = ({
  title = "Content Details",
  fields,
  onChange,
  onSave,
  saving = false,
  showSaveButton = false,
  saveButtonColor,
}) => {
  const [draftValues, setDraftValues] = useState<Record<string, any>>({});
  const lastExternalValues = React.useRef<Record<string, any>>({});

  useEffect(() => {
    const nextDraft = { ...draftValues };
    let hasExternalChange = false;

    fields.forEach((field) => {
      const incoming = field.value ?? "";
      const previous = lastExternalValues.current[field.field];

      // File inputs cannot have their value set programmatically.
      if (field.type === "file") {
        lastExternalValues.current[field.field] = incoming;
        return;
      }

      if (incoming !== previous) {
        nextDraft[field.field] =
          field.type === "datetime"
            ? toDateTimeLocal(incoming)
            : incoming;

        hasExternalChange = true;
      }

      lastExternalValues.current[field.field] = incoming;
    });

    if (hasExternalChange) {
      setDraftValues(nextDraft);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const commitValue = (field: FieldConfig) => {
    let value = draftValues[field.field];

    switch (field.type) {
      case "integer":
        value = value === "" ? null : parseInt(value, 10);
        break;

      case "float":
        value = value === "" ? null : parseFloat(value);
        break;

      case "boolean":
        value = Boolean(value);
        break;

      default:
        break;
    }

    onChange?.(field.field, value);
  };

  return (
    <>
      <Stack spacing={2}>
        {fields.map((field) => {
          const type = field.type ?? "string";

          if (type === "boolean") {
            return (
              <FormControlLabel
                key={field.field}
                label={field.label}
                control={
                  <Switch
                    checked={Boolean(draftValues[field.field])}
                    disabled={saving}
                    onChange={(event) =>
                      setDraftValues((previous) => ({
                        ...previous,
                        [field.field]: event.target.checked,
                      }))
                    }
                    onBlur={() => commitValue(field)}
                  />
                }
              />
            );
          }

          if (type === "file") {
            return (
              <FileUploadField
                key={field.field}
                field={field}
                saving={saving}
                onChange={onChange}
              />
            );
          }

          if (type === "select") {
            const options = field.options ?? [];

            return (
              <TextField
                key={field.field}
                label={field.label}
                fullWidth
                select
                value={draftValues[field.field] ?? ""}
                disabled={saving}
                onChange={(event) => {
                  const nextValue = event.target.value;

                  setDraftValues((previous) => ({
                    ...previous,
                    [field.field]: nextValue,
                  }));

                  onChange?.(
                    field.field,
                    nextValue === "" ? null : nextValue
                  );
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {options.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    value={String(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

          return (
            <TextField
              key={field.field}
              label={field.label}
              fullWidth
              multiline={field.multiline}
              rows={field.rows}
              value={draftValues[field.field] ?? ""}
              disabled={saving}
              type={
                type === "integer" || type === "float"
                  ? "number"
                  : type === "date"
                    ? "date"
                    : type === "datetime"
                      ? "datetime-local"
                      : "text"
              }
              InputLabelProps={
                type === "date" || type === "datetime"
                  ? { shrink: true }
                  : undefined
              }
              onChange={(event) =>
                setDraftValues((previous) => ({
                  ...previous,
                  [field.field]: event.target.value,
                }))
              }
              onBlur={() => commitValue(field)}
            />
          );
        })}
      </Stack>

      {showSaveButton && onSave && (
        <Button
          variant="contained"
          sx={{
            mt: 3,
            backgroundColor: saveButtonColor,
          }}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      )}
    </>
  );
};

export default ContentDetails;